import { type LucideIcon } from 'lucide-react';
import { cn } from '@/client/lib/utils';

interface EmptyStateProps {
  /** The icon to display */
  icon: LucideIcon;
  /** Main heading text */
  title: string;
  /** Descriptive text below the title */
  description: string;
  /** Optional action button */
  action?: {
    label: string;
    onClick: () => void;
    icon?: LucideIcon;
  };
  /** Optional secondary action (text link style) */
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Custom gradient for the icon background */
  iconGradient?: string;
  /** Additional className for the container */
  className?: string;
}

/**
 * Reusable empty state component for when there's no data to display.
 *
 * @example
 * <EmptyState
 *   icon={Link2}
 *   title="No links yet"
 *   description="Create your first link to start tracking clicks."
 *   action={{
 *     label: "Create Link",
 *     onClick: () => openModal(),
 *     icon: Plus
 *   }}
 * />
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  size = 'md',
  iconGradient = 'from-primary/10 to-[hsl(172,66%,50%)]/10',
  className,
}: EmptyStateProps) {
  const sizeStyles = {
    sm: {
      container: 'p-8',
      iconWrapper: 'h-14 w-14 rounded-xl',
      icon: 'h-7 w-7',
      title: 'text-base',
      description: 'text-sm max-w-xs',
      button: 'px-4 py-2 text-sm',
    },
    md: {
      container: 'p-12',
      iconWrapper: 'h-20 w-20 rounded-2xl',
      icon: 'h-10 w-10',
      title: 'text-lg',
      description: 'text-sm max-w-sm',
      button: 'px-5 py-2.5 text-sm',
    },
    lg: {
      container: 'p-16',
      iconWrapper: 'h-24 w-24 rounded-3xl',
      icon: 'h-12 w-12',
      title: 'text-xl',
      description: 'text-base max-w-md',
      button: 'px-6 py-3 text-sm',
    },
  };

  const styles = sizeStyles[size];

  return (
    <div className={cn('text-center', styles.container, className)}>
      {/* Icon */}
      <div
        className={cn(
          'bg-gradient-to-br flex items-center justify-center mx-auto mb-4',
          iconGradient,
          styles.iconWrapper
        )}
      >
        <Icon className={cn('text-primary/50', styles.icon)} />
      </div>

      {/* Title */}
      <h3 className={cn('font-semibold mb-2', styles.title)}>{title}</h3>

      {/* Description */}
      <p className={cn('text-muted-foreground mx-auto', styles.description, action ? 'mb-6' : '')}>
        {description}
      </p>

      {/* Actions */}
      {(action || secondaryAction) && (
        <div className="flex flex-col items-center gap-3">
          {action && (
            <button
              onClick={action.onClick}
              className={cn(
                'inline-flex items-center gap-2 rounded-xl bg-primary font-semibold text-primary-foreground shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all',
                styles.button
              )}
            >
              {action.icon && <action.icon className="h-4 w-4" />}
              {action.label}
            </button>
          )}
          {secondaryAction && (
            <button
              onClick={secondaryAction.onClick}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Empty state variant specifically for search results
 */
interface SearchEmptyStateProps {
  searchQuery: string;
  onClear?: () => void;
  entityName?: string;
}

export function SearchEmptyState({
  searchQuery,
  onClear,
  entityName = 'results',
}: SearchEmptyStateProps) {
  return (
    <div className="p-12 text-center">
      <div className="h-16 w-16 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4">
        <svg
          className="h-8 w-8 text-muted-foreground/50"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
          />
        </svg>
      </div>
      <h3 className="text-lg font-semibold mb-2">No {entityName} found</h3>
      <p className="text-muted-foreground text-sm max-w-sm mx-auto mb-4">
        No {entityName} match &quot;
        <span className="font-medium text-foreground">{searchQuery}</span>&quot;. Try adjusting your
        search.
      </p>
      {onClear && (
        <button onClick={onClear} className="text-sm text-primary hover:underline">
          Clear search
        </button>
      )}
    </div>
  );
}

/**
 * Empty state for onboarding new users with step-by-step guidance
 */
interface OnboardingEmptyStateProps {
  icon: LucideIcon;
  title: string;
  steps: Array<{
    icon: LucideIcon;
    text: string;
  }>;
  action: {
    label: string;
    onClick: () => void;
    icon?: LucideIcon;
  };
  className?: string;
}

export function OnboardingEmptyState({
  icon: Icon,
  title,
  steps,
  action,
  className,
}: OnboardingEmptyStateProps) {
  return (
    <div className={cn('p-12 text-center', className)}>
      {/* Icon */}
      <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mx-auto mb-6">
        <Icon className="h-10 w-10 text-primary" />
      </div>

      {/* Title */}
      <h3 className="text-xl font-semibold mb-6">{title}</h3>

      {/* Steps */}
      <div className="flex flex-col items-center gap-3 mb-8 max-w-sm mx-auto">
        {steps.map((step, index) => (
          <div
            key={index}
            className="flex items-center gap-3 w-full text-left p-3 rounded-xl bg-muted/30"
          >
            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <step.icon className="h-4 w-4 text-primary" />
            </div>
            <span className="text-sm text-muted-foreground">{step.text}</span>
          </div>
        ))}
      </div>

      {/* Action */}
      <button
        onClick={action.onClick}
        className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
      >
        {action.icon && <action.icon className="h-5 w-5" />}
        {action.label}
      </button>
    </div>
  );
}
